// components/pwa/installPrompt.js
//
// Cross-platform "install the app" plumbing, shared by the admin dashboard and
// the owner portal.
//
// - Android / desktop Chrome: the browser fires `beforeinstallprompt` ONCE,
//   early (often before lazy React chunks load). We capture and stash it at
//   module level — initInstallCapture() must be called from an EAGER entry
//   point (AppRouter) — so a later button click can trigger the REAL native
//   install dialog via promptInstall().
// - iOS Safari: Apple exposes NO install API. The only path is the manual
//   Share → "Add to Home Screen" gesture; the UI shows a visual guide instead
//   (see InstallAppButton). isIOSNoApi() detects that case.
// - Already installed (standalone display mode): nothing to offer.

let deferredPrompt = null;
let captured = false;
const listeners = new Set();

export const initInstallCapture = () => {
  if (captured || typeof window === 'undefined') return;
  captured = true;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // keep the mini-infobar away; we prompt on OUR button
    deferredPrompt = e;
    listeners.forEach((fn) => fn());
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    listeners.forEach((fn) => fn());
  });
};

/** Subscribe to availability changes (returns an unsubscribe fn). */
export const onInstallAvailabilityChange = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** App already running from the home screen? */
export const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true;

/** iOS device where no install API exists (guide-only path). */
export const isIOSNoApi = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !isStandalone();

/** Native one-tap install available right now? (Android / desktop Chrome) */
export const canNativeInstall = () => !!deferredPrompt;

/** Trigger the REAL native install dialog. Returns 'accepted' | 'dismissed' | 'unavailable'. */
export const promptInstall = async () => {
  if (!deferredPrompt) return 'unavailable';
  const evt = deferredPrompt;
  deferredPrompt = null; // a prompt event is single-use
  evt.prompt();
  const { outcome } = await evt.userChoice;
  return outcome; // 'accepted' | 'dismissed'
};
